// Il filo con il caro, in una forma sola: il tipo delle «novità» e i tre pezzi di
// interfaccia (icona, frase, bottone di ascolto) che Home, «Novità» e «La tua posta»
// devono mostrare identici. Il nome del caro e la voce arrivano da chi la costruisce:
// `caro` cambia quando /api/state risponde, perciò è una funzione e non una stringa.
import { el } from './ui.js';
import { playUrl } from './tts.js';

// Le «novità da Vincenzo»: reazioni non viste, sue domande, racconti degli ospiti.
export type Novita =
  | { type: 'reazione'; id: number; when: string; memoryId: number; title: string | null; kind: 'heart' | 'text' | 'voice'; text: string | null; mediaId: number | null; seen: boolean }
  // `memoryId`: il racconto che lei ha fatto rispondendo alla domanda, se l'ha fatto.
  | { type: 'domanda'; id: number; when: string; questionId: number; text: string; voiceUrl: string | null; memoryId?: number }
  | { type: 'voce'; id: number; when: string; memoryId: number; title: string | null; narrator: string; kind?: 'storia' | 'saluto'; nuovo?: boolean }
  // Una risposta nella conversazione col quaderno (di Vincenzo, o del quaderno che ha sistemato di notte).
  | { type: 'richiesta'; id: number; when: string; requestId: number; title: string | null; text: string; from: 'vincenzo' | 'quaderno' };

/** Solo nella posta: una risposta che lei ha scritto a un parente (mai «nuova»: è sua).
 *  `stato` come sotto la risposta nella storia: letta, mandata per email, o in attesa. */
export type RispostaOspitePosta = {
  type: 'risposta_ospite'; id: number; when: string; memoryId: number; title: string | null;
  guestName: string; text: string; stato: 'attesa' | 'vista' | 'email';
};

/** Una voce della posta: una novità che sa anche se lei l'ha già vista. */
export type VocePosta = (Novita | RispostaOspitePosta) & { nuovo: boolean };

/** La risposta di mamma appena inviata (per il «Ritira» finché lui non l'ha vista). */
export type Risposta = { id: number; kind: 'text' | 'voice'; text: string | null; media_id: number | null; created_at: string };

export function soloEmoji(text: string): boolean {
  const t = text.trim();
  // Emoji_Component comprende anche cifre, # e *: «1954» non è una faccina.
  return t.length > 0 && t.length <= 12 && !/[0-9#*]/.test(t)
    && /\p{Extended_Pictographic}/u.test(t)
    && /^[\p{Extended_Pictographic}\p{Emoji_Component}\s️‍]+$/u.test(t);
}

type Contesto = {
  /** Il nome del caro al momento dell'uso (cambia quando arriva /api/state). */
  caro: () => string;
  speak: (text: string, opts?: { force?: boolean }) => Promise<void>;
};

export function filoUI(ctx: Contesto): {
  iconaNovita: (n: Novita) => string;
  fraseNovita: (n: Novita, breve?: boolean) => string;
  azioneNovita: (n: Novita, cls?: string) => HTMLElement | null;
} {
  function iconaNovita(n: Novita): string {
    if (n.type === 'domanda') return '❓';
    if (n.type === 'voce') return n.kind === 'saluto' ? '💌' : '🎤';
    if (n.type === 'richiesta') return '📝';
    if (n.kind === 'heart') return '♥';
    if (n.kind === 'voice') return '🎙️';
    return n.text && soloEmoji(n.text) ? n.text.trim() : '✉️';
  }

  function fraseNovita(n: Novita, breve = false): string {
    const caro = ctx.caro();
    if (n.type === 'domanda') return `${caro} ti ha fatto una domanda: “${breve && n.text.length > 90 ? n.text.slice(0, 90) + '…' : n.text}”`;
    if (n.type === 'voce') {
      return n.kind === 'saluto'
        ? `${n.narrator} ti ha lasciato un saluto`
        : `${n.narrator} ha lasciato un racconto nel tuo quaderno${n.title ? `: «${n.title}»` : ''}`;
    }
    if (n.type === 'richiesta') {
      const chi = n.from === 'quaderno' ? 'Il tuo quaderno ha sistemato una cosa che avevi chiesto' : `${caro} ti ha risposto su una cosa che avevi chiesto al quaderno`;
      const su = n.title ? ` («${n.title}»)` : '';
      return breve ? `${chi}${su}: “${n.text.length > 90 ? n.text.slice(0, 90) + '…' : n.text}”` : `${chi}${su}`;
    }
    const storia = n.title ? `«${n.title}»` : 'una tua storia';
    if (n.kind === 'heart') return `A ${caro} è piaciuta tanto ${storia} ♥`;
    if (n.kind === 'voice') return `${caro} ti ha lasciato un messaggio vocale su ${storia}`;
    const testo = n.text ?? '';
    if (soloEmoji(testo)) return `${caro} ti manda ${testo.trim()} per ${storia}`;
    return breve
      ? `${caro} ti ha scritto su ${storia}: “${testo.length > 80 ? testo.slice(0, 80) + '…' : testo}”`
      : `${caro} ti ha scritto su ${storia}`;
  }

  /** Bottone di ascolto/lettura adatto alla novità (o niente, per il cuore). */
  function azioneNovita(n: Novita, cls = 'btn-mini'): HTMLElement | null {
    if (n.type === 'reazione' && n.kind === 'voice' && n.mediaId) {
      return el('button', { class: cls, click: () => void playUrl(`/api/media/${n.mediaId}`) }, '▶  Ascolta');
    }
    if (n.type === 'reazione' && n.kind === 'text' && n.text && !soloEmoji(n.text)) {
      return el('button', { class: cls, click: () => void ctx.speak(n.text!, { force: true }) }, '🔊  Leggimelo');
    }
    if (n.type === 'domanda' && n.voiceUrl) {
      return el('button', { class: cls, click: () => void playUrl(n.voiceUrl!) }, '▶  La sua voce');
    }
    if (n.type === 'richiesta' && n.text) {
      return el('button', { class: cls, click: () => void ctx.speak(n.text, { force: true }) }, '🔊  Leggimelo');
    }
    return null;
  }

  return { iconaNovita, fraseNovita, azioneNovita };
}
